import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { AbstractMedia, MediaType } from 'types/AbstractMedia';
import { INACTIVITY_TIME_ON_HOME } from 'modules/auth/constants';

import useInactivity from 'hooks/useInactivity';
import useOrientation, { Orientation } from 'hooks/useOrientation';
import useStableMedia, { getMediaIdentity } from 'hooks/useStableMedia';

import { hasScreensaver } from 'modules/dealers/selectors';

import { Spinner } from 'components/ui';
import Screensaver from 'components/Screensaver';

import BackgroundVideo from './BackgroundVideo';
import resolveBackgroundMedia from './resolveBackgroundMedia';

interface BackgroundProps {
  loading?: boolean;
  landscapeMedia?: AbstractMedia;
  portraitMedia?: AbstractMedia;
}

const MEDIA_CLASSNAMES = 'fixed object-cover w-screen h-screen';

// getMediaIdentity is typed for a possibly-undefined media; a video candidate is always defined here
const getVideoCandidateKey = (media: AbstractMedia): string => getMediaIdentity(media) as string;

const Background: React.FC<React.PropsWithChildren<BackgroundProps>> = ({
  landscapeMedia, portraitMedia, loading, children,
}) => {
  const orientation = useOrientation();
  const landscapeCandidate = resolveBackgroundMedia(Orientation.LANDSCAPE, landscapeMedia, portraitMedia);
  const portraitCandidate = resolveBackgroundMedia(Orientation.PORTRAIT, landscapeMedia, portraitMedia);
  const orientationMedia = orientation === Orientation.PORTRAIT ? portraitCandidate : landscapeCandidate;
  const media = useStableMedia(orientationMedia);
  const allowScreensaver = useSelector(hasScreensaver);

  const [readyImageKey, setReadyImageKey] = React.useState<string>();
  const [isActiveVideoReady, setIsActiveVideoReady] = React.useState<boolean>(false);
  const [hasActiveVideoPoster, setHasActiveVideoPoster] = React.useState<boolean>(false);
  const [screensaverActive, setScreensaverActive] = React.useState(false);
  const [forceDefaultBackground, setForceDefaultBackground] = React.useState(false);

  const mediaKey = getMediaIdentity(media);
  const isImageReady = readyImageKey === mediaKey;

  const isImage = media?.type === MediaType.IMAGE;
  const isVideo = media?.type === MediaType.VIDEO;
  const isCustom = !forceDefaultBackground && Boolean(media);

  const isLoadingImage = isImage && !isImageReady;
  const isLoadingVideo = isVideo && !isActiveVideoReady && !hasActiveVideoPoster;
  const isLoading = loading || (isCustom && (isLoadingVideo || isLoadingImage));

  const videoCandidates = [landscapeCandidate, portraitCandidate].reduce<AbstractMedia[]>((acc, candidate) => {
    if (candidate?.type !== MediaType.VIDEO) {
      return acc;
    }
    if (acc.some((existing) => getVideoCandidateKey(existing) === getVideoCandidateKey(candidate))) {
      return acc;
    }
    return [...acc, candidate];
  }, []);

  // Mount a video the first time its orientation becomes active, so the other format isn't preloaded until it's needed
  const activatedVideoKeysRef = React.useRef<Set<string>>(new Set());
  if (isVideo && mediaKey && !activatedVideoKeysRef.current.has(mediaKey)) {
    activatedVideoKeysRef.current = new Set(activatedVideoKeysRef.current).add(mediaKey);
  }

  const activatedVideoCandidates = videoCandidates.filter(
    (candidate) => activatedVideoKeysRef.current.has(getVideoCandidateKey(candidate)),
  );

  const handleError = React.useCallback(() => setForceDefaultBackground(true), []);

  React.useLayoutEffect(() => {
    // Reset the background if the media is changed
    if (media) {
      setForceDefaultBackground(false);
    }
  }, [media]);

  const handleImageLoad = React.useCallback(() => {
    setReadyImageKey(mediaKey);
  }, [mediaKey]);

  const showScreensaver = React.useCallback(() => setScreensaverActive(true), []);
  const { restart } = useInactivity({
    seconds: INACTIVITY_TIME_ON_HOME,
    onInactive: showScreensaver,
    paused: !allowScreensaver
  });

  const hideScreensaver = React.useCallback(() => {
    setScreensaverActive(false);
    restart();
  }, [restart]);

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center min-h-screen">
          <Spinner />
        </div>
      )}
      <div
        className={classNames('flex flex-col items-center justify-start', {
          homePage: !isLoading,
          'bg-cover bg-center bg-home -z-10': !isLoading && !isCustom,
        })}
      >
        {isCustom && (
          <>
            {!isVideo && (
              <picture className="w-screen h-screen fixed">
                {portraitCandidate?.type === MediaType.IMAGE && (
                  <source media="(orientation: portrait)" srcSet={portraitCandidate.path} />
                )}
                {landscapeCandidate?.type === MediaType.IMAGE && (
                  <source media="(orientation: landscape)" srcSet={landscapeCandidate.path} />
                )}
                <img
                  alt=""
                  loading="eager"
                  src={media.path}
                  fetchPriority="high"
                  onError={handleError}
                  onLoad={handleImageLoad}
                  className={MEDIA_CLASSNAMES}
                  data-testid="background-image"
                />
              </picture>
            )}
            {activatedVideoCandidates.map((candidate) => (
              <BackgroundVideo
                key={getVideoCandidateKey(candidate)}
                media={candidate}
                active={getVideoCandidateKey(candidate) === mediaKey}
                className={MEDIA_CLASSNAMES}
                onError={handleError}
                onReadyChange={setIsActiveVideoReady}
                onPosterChange={setHasActiveVideoPoster}
              />
            ))}
          </>
        )}
        {!isLoading && children}
        {screensaverActive && (
          <div className={classNames(MEDIA_CLASSNAMES, 'z-max')}>
            <Screensaver onClose={hideScreensaver} />
          </div>
        )}
      </div>
    </>
  );
};

export default Background;
